import escape from 'lodash/escape';
import isEqual from 'lodash/isEqual';
import { override, makeObservable } from 'mobx';

import type { Optional } from 'chaika-common-types';
import { DictionaryNames, EntryTypeFieldName, FieldStatus } from 'chaika-enums';

import { store } from '@store';
import { AllergyList, AllergyParams } from '@store/models/SystemFields/Allergy';
import { AbstractListField, ListValueField } from './AbstractListField';
import { translate } from '@chaika/i18n/helper';

export const NOT_USED_ALLERGY_ITEM: Partial<AllergyParams> = {
    id: 'f3bf3254-61d2-41d3-ab28-423e9bb39327',
    allergen: '',
    reactionIds: [],
    customReactions: [],
    status: FieldStatus.Active,
    isDefault: true,
};

export class AllergyField extends AbstractListField<AllergyList, AllergyParams> {
    constructor(params: AllergyList | AllergyParams[], name: string) {
        super(params, name);
        makeObservable(this);
    }

    public get hasHtmlPreview(): boolean {
        return true;
    }

    @override
    public get hasData(): boolean {
        return this.fieldValue.values.filter(value => value.status !== FieldStatus.Deleted).length > 0;
    }

    public isEqual(value: AllergyList): boolean {
        const allergies = this.makeUpdateParams();

        return isEqual(allergies, value.serialize());
    }

    public makeUpdateParams() {
        return this.fieldValue.serialize();
    }

    public getPreview(): string {
        const serializedAllergyList = this.fieldValue.serialize().filter(it => it.status !== FieldStatus.Deleted);
        const isDrugAllergy = this.fieldName === EntryTypeFieldName.DrugAllergy;

        return serializedAllergyList.map(allergy => this.getAllergyRevision(allergy, isDrugAllergy)).join('\n');
    }

    public getItemRevision(id: string): string {
        const allergy = this.fieldValue.getExisting(id);

        const { reactions } = this.getAllergyPreviewParams(allergy);

        return reactions;
    }

    public getItemTitle(id: string): string {
        const allergy = this.fieldValue.get(id);

        return allergy?.allergen ?? '';
    }

    public getStatus(id: string): Optional<string> {
        const allergy = this.fieldValue.getExisting(id);

        return allergy.status === FieldStatus.Deleted ? translate('deleted') : undefined;
    }

    public getAllergyRevision(allergy: AllergyParams, isDrugAllergy?: boolean): string {
        let revisionString: string;

        if (allergy.isDefault) {
            revisionString = `<strong>${translate(isDrugAllergy ? 'noDrugAllergySecond' : 'noAllergy')}</strong>`;
        } else {
            const { title, reactions } = this.getAllergyPreviewParams(allergy);
            revisionString = `<strong>${escape(title)}:</strong> ${escape(reactions)}`;
        }
        return revisionString;
    }

    public getAllergyPreviewParams(allergy: AllergyParams): { title: string; reactions: string } {
        const { dictionary } = store;

        const allergyReactionDictionary = dictionary.getDictionary(DictionaryNames.AllergyReaction);

        const dictionaryReactions = allergy.reactionIds.map(id => {
            const reaction = allergyReactionDictionary.get(id);

            return reaction?.title ?? '-';
        });

        const reactions =
            [...dictionaryReactions, ...allergy.customReactions].join(', ') || translate('noReactionType');

        return {
            reactions,
            title: allergy.allergen,
        };
    }

    protected setDefaultValueValue(): void {
        this.defaultNullValue = [];
    }

    protected setFieldValue(value: ListValueField<AllergyParams>): void {
        const formattedValues = this.makeValueArray(value);

        this.fieldValue = new AllergyList(formattedValues);
    }
}
